import type { Connector, ConnectorType } from '@/types'
import { CONNECTOR_TYPES } from './connectorConstants'
import { ConnectorCard, type ConnectorCardSection } from './ConnectorCard'

interface ConnectorCatalogProps {
  connectors: Connector[]
  onCardClick: (type: ConnectorType) => void
  onAddClick: (type: ConnectorType) => void
  canManage: boolean
  sonarQubeEnabled?: boolean
  unavailableReasons?: Partial<Record<ConnectorType, string>>
}

const SCROLL_AFTER_CARD_COUNT = 6

export function ConnectorCatalog({
  connectors,
  onCardClick,
  onAddClick,
  canManage,
  sonarQubeEnabled = false,
  unavailableReasons = {},
}: Readonly<ConnectorCatalogProps>) {
  const instanceCount = (type: ConnectorType) =>
    type === 'SonarQube' ? (sonarQubeEnabled ? 1 : 0) : connectors.filter((c) => c.type === type).length

  const enabledInstanceCount = (type: ConnectorType) =>
    type === 'SonarQube' ? (sonarQubeEnabled ? 1 : 0) : connectors.filter((c) => c.type === type && c.isEnabled).length

  const isEnabled = (type: ConnectorType) => enabledInstanceCount(type) > 0

  const enabledTypes = CONNECTOR_TYPES.filter(isEnabled)

  const availableTypes = CONNECTOR_TYPES.filter((type) => !isEnabled(type))

  const renderCard = (type: ConnectorType, section: ConnectorCardSection) => (
    <ConnectorCard
      key={type}
      connectorType={type}
      section={section}
      onClick={() => onCardClick(type)}
      onAddClick={() => onAddClick(type)}
      canManage={canManage}
      isConnectedOverride={type === 'SonarQube' ? sonarQubeEnabled : undefined}
      hasDisabledInstances={instanceCount(type) > enabledInstanceCount(type)}
      enabledInstanceCount={enabledInstanceCount(type)}
      unavailableReason={unavailableReasons[type]}
    />
  )

  const renderGrid = (types: ConnectorType[], section: ConnectorCardSection) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {types.map((type) => renderCard(type, section))}
    </div>
  )

  return (
    <div className="flex flex-col gap-8">
      {enabledTypes.length > 0 && (
        <section className="flex flex-col gap-3" data-testid="connectors-enabled-section">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Enabled
          </h2>
          {enabledTypes.length > SCROLL_AFTER_CARD_COUNT ? (
            <div className="max-h-[calc(2*11rem)] overflow-y-auto pr-1" data-testid="enabled-connectors-scroll">
              {renderGrid(enabledTypes, 'enabled')}
            </div>
          ) : (
            renderGrid(enabledTypes, 'enabled')
          )}
        </section>
      )}

      <section className="flex flex-col gap-3" data-testid="connectors-available-section">
        {enabledTypes.length > 0 && (
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Available
          </h2>
        )}
        {renderGrid(availableTypes, 'available')}
      </section>
    </div>
  )
}
